import { CLASS_CATALOG } from '@/features/class/classCatalog';
import { useClassManageStore } from '@/features/class/manage/classManageStore';
import { useClassMembershipStore } from '@/features/class/classMembershipStore';
import { MOCK_CURRENT_USER_PROFILE, MOCK_OWNED_CLASSES } from '@/features/profile/mockData';
import {
  MOCK_OTHER_USER_CLASSES,
  MOCK_OTHER_USER_PROFILE,
  MOCK_PRIVATE_USER_CLASSES,
  MOCK_PRIVATE_USER_PROFILE,
} from '@/features/profile/mockProfiles';

export interface ClassOwnerRef {
  readonly username: string;
  readonly name: string;
}

const OWNER_BY_CODE = new Map<string, ClassOwnerRef>();

function registerOwner(code: string, owner: ClassOwnerRef) {
  OWNER_BY_CODE.set(code.toUpperCase(), owner);
}

for (const classItem of MOCK_OWNED_CLASSES) {
  registerOwner(classItem.code, {
    username: MOCK_CURRENT_USER_PROFILE.username,
    name: MOCK_CURRENT_USER_PROFILE.name,
  });
}

for (const classItem of MOCK_OTHER_USER_CLASSES) {
  registerOwner(classItem.code, {
    username: MOCK_OTHER_USER_PROFILE.username,
    name: MOCK_OTHER_USER_PROFILE.name,
  });
}

for (const classItem of MOCK_PRIVATE_USER_CLASSES) {
  registerOwner(classItem.code, {
    username: MOCK_PRIVATE_USER_PROFILE.username,
    name: MOCK_PRIVATE_USER_PROFILE.name,
  });
}

for (const entry of CLASS_CATALOG) {
  if (!OWNER_BY_CODE.has(entry.code.toUpperCase())) {
    registerOwner(entry.code, {
      username: MOCK_CURRENT_USER_PROFILE.username,
      name: MOCK_CURRENT_USER_PROFILE.name,
    });
  }
}

export function getClassOwnerByCode(classCode: string): ClassOwnerRef | undefined {
  const fromMap = OWNER_BY_CODE.get(classCode.toUpperCase());
  if (fromMap) {
    return fromMap;
  }

  const managed = useClassManageStore.getState().getManagedClassByCode(classCode);
  if (managed) {
    return {
      username: MOCK_CURRENT_USER_PROFILE.username,
      name: MOCK_CURRENT_USER_PROFILE.name,
    };
  }

  return undefined;
}

export function isCurrentUserClassOwner(classCode: string): boolean {
  const owner = getClassOwnerByCode(classCode);
  return owner?.username.toLowerCase() === MOCK_CURRENT_USER_PROFILE.username.toLowerCase();
}

export function resolveClassIdByCode(classCode: string): string {
  const normalized = classCode.toUpperCase();
  const managed = useClassManageStore.getState().getManagedClassByCode(classCode);
  if (managed) {
    return managed.id;
  }

  const owned =
    MOCK_OWNED_CLASSES.find((item) => item.code.toUpperCase() === normalized) ??
    MOCK_OTHER_USER_CLASSES.find((item) => item.code.toUpperCase() === normalized) ??
    MOCK_PRIVATE_USER_CLASSES.find((item) => item.code.toUpperCase() === normalized);

  if (owned) {
    return owned.id;
  }

  return `catalog-${normalized}`;
}

export function canAccessClass(classCode: string): boolean {
  if (isCurrentUserClassOwner(classCode)) {
    return true;
  }
  const classId = resolveClassIdByCode(classCode);
  return useClassMembershipStore.getState().isJoined(classId);
}
